// Cliente HTTP do portal — cópia enxuta de apps/frontend/src/lib/api.ts apontando
// pra /api/ss (routers/ss_portal.py). Cookie ss_session vai junto (same-origin).
const BASE = import.meta.env.VITE_API_BASE ?? "/api/ss";

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message?: string) {
    super(message ?? `API error ${status}`);
  }
  get detail(): string | undefined {
    const p = this.payload as { detail?: unknown } | null;
    return typeof p?.detail === "string" ? p.detail : undefined;
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 15_000): Promise<T> {
  const attempt = () => fetch(`${BASE}${path}`, {
    credentials: "include",
    signal: AbortSignal.timeout(timeoutMs),
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  let resp: Response;
  try {
    resp = await attempt();
  } catch (e) {
    const isGet = !init.method || init.method === "GET";
    const transient = (e instanceof DOMException && e.name === "TimeoutError") || e instanceof TypeError;
    if (!isGet || !transient) throw e;
    resp = await attempt();  // GET idempotente: 1 retry em conexão nova (keep-alive morto)
  }
  if (!resp.ok) {
    let body: unknown = null;
    try { body = await resp.json(); } catch { /* sem corpo */ }
    throw new ApiError(resp.status, body);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  // timeoutMs: o turno do chat de teste espera o LLM com tools (até 300 s, como o playground do Valquiria)
  post: <T>(path: string, body: unknown, opts?: { timeoutMs?: number }) => request<T>(path, { method: "POST", body: JSON.stringify(body) }, opts?.timeoutMs),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (path: string) => request<void>(path, { method: "DELETE" }),
};

// ── tipos do /api/ss ────────────────────────────────────────────────────────
export interface Me { account_id: string; name: string; max_agents: number }
export interface AgentSummary { id: string; name: string; published: boolean; has_unpublished_changes: boolean; updated_at: string }
export type FieldType = "text" | "textarea" | "slider" | "select" | "multi" | "bool" | "list" | "hours" | "faq";
export interface FieldOption { id: string; label: string; text: string }
export interface Field {
  id: string;  // identidade estável: answers são por id (key é só o nome do chip no prompt)
  key: string; label: string; type: FieldType; description?: string; default?: any;
  limit?: number | null; min?: number; max?: number; step?: number; min_label?: string; max_label?: string;
  options?: FieldOption[]; max_choices?: number | null; display?: "cards" | "dropdown";
  other?: boolean; other_limit?: number | null;  // opção "Outros": id OTHER + texto em answers["<id>:outro"]
  yes_text?: string; no_text?: string; max_items?: number | null;
  q_limit?: number | null; a_limit?: number | null;  // faq: teto de caracteres da pergunta/resposta; resposta = [{q, a}]
}
export const OTHER = "__outro__";
// hours: {"0".."6" (0 = segunda): [["08:00","11:00"], ...]} — dia ausente = fechado.
// Tool global como sessão de bloco (kind: "tool"): campos editáveis já mesclados com os
// overrides salvos + widget de estoque quando a tool usa {{ sistema.estoque }}.
export interface EditableField { path: string; label: string; help?: string; type: "text" | "number" | "bool" | "textarea"; value: any }
export interface EstoqueState {
  state: "disconnected" | "connecting" | "connected" | "error";
  fonte: string | null; urls: string[]; vehicle_count: number; last_updated_at: string | null;
  error: { friendly: string; code: string | null } | null; shared_by: string[]; had: boolean;
}
export interface Fonte { key: string; label: string; tutorial_html: string; multi_url: boolean }
export interface ToolCell {
  id: string; kind: "tool"; name: string; description: string; width: number; fields: Field[];
  editable?: EditableField[]; estoque?: EstoqueState | null;
}
export type Session = ToolCell;  // nome legado (EstoqueWidget)
// Célula = UM campo editável (aba Campos do global) ou uma tool global.
export interface FieldCell { id: string; kind: "field"; width: number; field: Field }
// Sub-bloco: os campos de dentro viram um card só (título/ajuda opcionais no topo). Só no 1º nível.
export interface GroupCell { id: string; kind: "group"; title: string; description: string; width: 100; rows: Leaf[][] }
// Item fixo de sistema: a config de follow-up do agente do cliente (sempre 100%).
export interface FollowupCell { id: "system:followup"; kind: "followup"; width: 100 }
export type Leaf = FieldCell | ToolCell | FollowupCell;
export type Cell = Leaf | GroupCell;
// Follow-up editável pelo cliente (o resto vem do global): mesma forma de agents.followup.
export type TplParam = string | { ai: string };
export interface FollowupStep { after_minutes: number; unit: "min" | "h"; template: { name: string; language: string; params: TplParam[] } | null; prompt: string }
export interface ClientFollowup {
  enabled: boolean; steps: FollowupStep[];
  schedule: { enabled: boolean; days: number[]; start: string; end: string };
  end_action: "nenhuma" | "crm" | "notificar";
}
// rows = layout do builder: cada linha soma até 100 de width (25/50/75/100 por célula).
export interface Block { key: string; title: string; description?: string; rows: Cell[][] }
export interface AgentDetail extends AgentSummary { blocks: Block[]; answers: Record<string, any>; new_field_ids: string[]; followup: ClientFollowup }
